<template>
  <div
    class="yard-view-area-select-wrapper d-flex flex-column gap-2"
    data-testid="yard-view-area-select-wrapper">
    <v-autocomplete
      class="yard-view-area-select"
      color="primary"
      density="compact"
      item-title="name"
      item-value="id"
      variant="outlined"
      :clearable="clearable"
      :disabled="disabled"
      :hide-details="hideDetails"
      :hint="hint"
      :items="spotAreas"
      :model-value="modelValue"
      :multiple="multiple"
      :persistent-hint="persistentHint"
      :placeholder="placeholder"
      :required="required"
      :rules="rules"
      @update:model-value="handleValueChange">
      <template #no-data>
        <v-list-item :title="t('yard.messages.noAreasAvailable')"></v-list-item>
      </template>

      <template #selection="{ index }">
        <span v-if="index === 0" class="typography-body-md">
          {{ getRenderedSelectedValue() }}
        </span>
      </template>

      <template #prepend-item v-if="multiple && spotAreas.length > 0">
        <v-list-item title="All areas" ripple @click="toggleSelectAll" class="typography-body-md">
          <template v-slot:prepend>
            <v-icon>{{ selectAllIcon }}</v-icon>
          </template>
        </v-list-item>
        <v-divider class="mt-2"></v-divider>
      </template>

      <template #item="{ item, props }">
        <v-list-item ripple v-bind="props" class="typography-body-md" :title="item.title">
          <template v-slot:prepend v-if="multiple">
            <v-icon>{{ getItemIcon(item) }}</v-icon>
          </template>

          <template v-slot:append v-if="multiple">
            <v-btn
              variant="text"
              size="small"
              class="text-none typography-button-sm ml-auto"
              @click="selectOnly($event, item)">
              {{ t('global.basicTerms.only') }}
            </v-btn>
          </template>
        </v-list-item>
      </template>

      <template #message="{ message }">
        <small class="typography-body-sm">
          {{ message }}
        </small>
      </template>
    </v-autocomplete>

    <!-- slot for any addition content.-->
    <slot
      v-if="modelValue && selectedAreas"
      name="selected-areas"
      :selected-area="selectedAreas"
      :selected-areas="selectedAreas"></slot>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import useYard from '@/modules/yard/composables/useYard';

export default defineComponent({
  name: 'YardViewAreaSelect',
  emits: ['update:modelValue', 'input'],
  props: {
    modelValue: {
      type: [Array, String, Number],
      default: () => []
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select an area'
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const yardStore = useYard();

    const areas = computed(() => yardStore.spotAreasRef.value || []);

    const handleValueChange = newValue => {
      emit('update:modelValue', newValue);
      emit('input', newValue);
    };

    const selectedAreas = computed(() => {
      if (!props.modelValue) {
        return null;
      }

      if (props.multiple) {
        const selectedAreas = Array.isArray(props.modelValue)
          ? props.modelValue
          : [props.modelValue];
        return selectedAreas.map(area => areas.value.find(a => a.id === area));
      }

      return areas.value.find(area => area.id === props.modelValue);
    });

    const getRenderedSelectedValue = () => {
      if (!selectedAreas.value) {
        return '';
      }

      if (props.multiple) {
        const selectedCount = selectedAreas.value.length;
        return `${selectedCount} ${t('global.basicTerms.of')} ${areas.value.length} ${t(
          'forms.common.terms.selected'
        )}`;
      }

      return selectedAreas.value.name;
    };

    const isAllSelected = computed(() => {
      if (!props.multiple) return false;
      const selected = Array.isArray(props.modelValue) ? props.modelValue : [];
      return selected.length === areas.value.length && areas.value.length > 0;
    });

    const selectAllIcon = computed(() => {
      if (isAllSelected.value) {
        return 'mdi-close-box';
      }
      const selected = Array.isArray(props.modelValue) ? props.modelValue : [];
      return selected.length > 0 ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline';
    });

    const toggleSelectAll = () => {
      if (!props.multiple) {
        return;
      }

      const newValue = isAllSelected.value ? [] : areas.value.map(area => area.id);
      handleValueChange(newValue);
    };

    const getItemIcon = item => {
      if (!props.multiple) {
        return '';
      }

      const selected = Array.isArray(props.modelValue) ? props.modelValue : [];
      return selected.includes(item.value) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline';
    };

    const selectOnly = (event, item) => {
      event.stopPropagation();

      if (!props.multiple) {
        return;
      }

      handleValueChange([item.value]);
    };

    return {
      t,
      spotAreas: areas,
      handleValueChange,
      selectedAreas,
      getRenderedSelectedValue,
      isAllSelected,
      selectAllIcon,
      toggleSelectAll,
      getItemIcon,
      selectOnly
    };
  }
});
</script>

<style lang="scss" scoped>
.yard-view-area-select {
  min-inline-size: 220px;
}
</style>
